import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../trpc';
import { useUser } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { JobListingCard } from '@/components/JobListingCard';
import { waitForClerk } from '@/utils/clerk';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, FileUp, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ResumeUploadDialog } from '@/components/ResumeUploadDialog';
import { useResume } from '@/hooks/useResume';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import { analytics } from '@/lib/analytics';

export const Route = createFileRoute('/dashboard/suggested-jobs')({
  component: SuggestedJobsContent,
  beforeLoad: async () => {
    // Wait for Clerk to load
    const user = await waitForClerk();
    if (!user) {
      return redirect({
        to: '/',
        replace: true,
      });
    }
  },
});

const JOBS_PER_PAGE = 9;

function SuggestedJobsContent() {
  const { t } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const userId = user?.id;
  const [sortBy, setSortBy] = useState<'newest' | 'score'>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [remoteOnly, setRemoteOnly] = useState(false);

  // Get current user's resume to check if they have one uploaded
  const { resume } = useResume({ componentName: 'SuggestedJobsContent' });
  const { trackEvent } = useActivityTracking();

  useEffect(() => {
    trackEvent('suggested_jobs_viewed');
    analytics.track('suggested_jobs_viewed');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine if user is on LITE tier (no AI scoring)
  const { hasProAccess, isLoading: isLoadingAccess } = useFeatureAccess();
  const isLiteTier = !isLoadingAccess && !hasProAccess;

  // Get recommended jobs
  const recommendedJobsQuery = trpc.jobListings.getRecommendedJobs.useQuery(
    { userId: userId ?? '', sortBy },
    {
      enabled: !!userId,
      retry: 2,
      retryDelay: 500,
      onError: (error: Error) => {
        console.error('Recommended jobs query error:', error);
        Sentry.captureException(error, {
          tags: { component: 'SuggestedJobsContent', action: 'recommendedJobsQuery', userId },
        });
      },
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      staleTime: 60000, // Job listings stay fresh for 1 minute
      refetchInterval: 300000, // Refetch every 5 minutes (syncs happen 2x daily anyway)
    }
  );

  // Fire suggested_jobs_generation_completed once per page visit, not per refetch.
  // refetchOnWindowFocus / refetchOnMount / refetchInterval / retry would
  // otherwise cause the same logical "user saw suggested jobs" event to
  // fire several times during a single session.
  const generationTrackedRef = useRef(false);
  useEffect(() => {
    if (generationTrackedRef.current) return;
    const data = recommendedJobsQuery.data;
    if (!data) return;
    const jobs = Array.isArray(data) ? data : [];
    const scoredJobs = jobs.filter((j: any) => typeof j?.score === 'number');
    const avgScore = scoredJobs.length
      ? scoredJobs.reduce((sum: number, j: any) => sum + j.score, 0) / scoredJobs.length
      : null;
    analytics.track('suggested_jobs_generation_completed', {
      jobCount: jobs.length,
      ...(avgScore != null ? { avgScore: Math.round(avgScore * 100) / 100 } : {}),
    });
    generationTrackedRef.current = true;
  }, [recommendedJobsQuery.data]);

  // Get all applications to check which jobs are already tracked
  const applicationsQuery = trpc.applications.getAll.useQuery(undefined, {
    enabled: !!userId,
    retry: 2,
    retryDelay: 500,
  });

  // Apply location filter, then paginate the filtered list.
  // The `location` field is a free-form string ("Austin, TX", "London, UK",
  // "Toronto, ON K1A 0B1", "Remote") so we substring-match against it plus
  // `country` to handle queries like "USA" or "United Kingdom".
  const allJobs = recommendedJobsQuery.data || [];
  const allApplications = applicationsQuery.data || [];
  const normalizedQuery = locationQuery.trim().toLowerCase();
  const filteredJobs = allJobs.filter((job: any) => {
    const location = (job.location ?? '').toLowerCase();
    if (remoteOnly && !location.includes('remote')) return false;
    if (normalizedQuery) {
      const country = (job.country ?? '').toLowerCase();
      if (!location.includes(normalizedQuery) && !country.includes(normalizedQuery)) {
        return false;
      }
    }
    return true;
  });
  const totalPages = Math.ceil(filteredJobs.length / JOBS_PER_PAGE);
  const startIndex = (currentPage - 1) * JOBS_PER_PAGE;
  const endIndex = startIndex + JOBS_PER_PAGE;
  const paginatedJobs = filteredJobs.slice(startIndex, endIndex);

  // Helper function to find existing application lookupId for a job
  const findExistingApplicationLookupId = (jobUrl: string) => {
    if (!jobUrl) return undefined;

    // Normalize the job URL
    let normalizedJobUrl = jobUrl;
    try {
      const urlObj = new URL(jobUrl);
      normalizedJobUrl = urlObj.toString();
    } catch (error) {
      // If URL parsing fails, use original
    }

    // Find application with matching urlSource
    const application = allApplications.find((app: any) => {
      if (!app.urlSource) return false;

      // Normalize application URL
      let normalizedAppUrl = app.urlSource;
      try {
        const urlObj = new URL(app.urlSource);
        normalizedAppUrl = urlObj.toString();
      } catch (error) {
        // If URL parsing fails, use original
      }

      return normalizedAppUrl === normalizedJobUrl;
    });

    return application?.lookupId;
  };

  // Keep currentPage valid as results/page-count change
  useEffect(() => {
    setCurrentPage((p) => {
      if (totalPages <= 1) return 1;
      return Math.min(Math.max(p, 1), totalPages);
    });
  }, [totalPages]);

  // Reset to page 1 when sort changes
  const handleSortChange = (newSort: 'newest' | 'score') => {
    setSortBy(newSort);
    setCurrentPage(1);
  };

  // Reset to page 1 whenever filter inputs change so the user lands on the
  // first page of the new result set instead of an arbitrary deep page.
  useEffect(() => {
    setCurrentPage(1);
  }, [locationQuery, remoteOnly]);

  const clearFilters = () => {
    setLocationQuery('');
    setRemoteOnly(false);
  };
  const hasActiveFilter = remoteOnly || normalizedQuery.length > 0;

  return (
    <>
      {!userId || recommendedJobsQuery.isLoading ? (
        <p>{t('dashboard.loadingJobs', 'Loading jobs...')}</p>
      ) : recommendedJobsQuery.error ? (
        <p className='text-red-500'>
          {t('common.genericError', 'Something went wrong. Please try again.')}
        </p>
      ) : (
        <>
          <div className='flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between'>
            <h2 className='text-xl font-bold'>{t('dashboard.suggestedJobs', 'Suggested Jobs')}</h2>
            {allJobs.length > 0 && (
              <div className='flex flex-wrap items-center gap-2'>
                <Input
                  type='search'
                  value={locationQuery}
                  onChange={(e) => setLocationQuery(e.target.value)}
                  placeholder={t(
                    'dashboard.filterLocationPlaceholder',
                    'Filter by city, state, postcode…'
                  )}
                  aria-label={t('dashboard.filterLocationLabel', 'Filter by location')}
                  className='w-full sm:w-56'
                />
                <label className='flex items-center gap-2 text-sm text-gray-700 select-none'>
                  <input
                    type='checkbox'
                    checked={remoteOnly}
                    onChange={(e) => setRemoteOnly(e.target.checked)}
                    className='h-4 w-4'
                  />
                  {t('dashboard.filterRemoteOnly', 'Remote only')}
                </label>
                <div className='flex gap-2'>
                  <button
                    type='button'
                    onClick={() => handleSortChange('newest')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      sortBy === 'newest'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {t('dashboard.sortNewest', 'Newest')}
                  </button>
                  <button
                    type='button'
                    onClick={() => handleSortChange('score')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      sortBy === 'score'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {t('dashboard.sortScore', 'By Score')}
                  </button>
                </div>
              </div>
            )}
          </div>
          {allJobs.length > 0 ? (
            filteredJobs.length === 0 ? (
              <div className='rounded-lg border border-gray-200 bg-gray-50 p-8 text-center'>
                <p className='mb-4 text-gray-700'>
                  {t(
                    'dashboard.noJobsMatchFilter',
                    'No suggested jobs match your filter.'
                  )}
                </p>
                {hasActiveFilter && (
                  <Button variant='outline' onClick={clearFilters}>
                    {t('dashboard.clearFilters', 'Clear filters')}
                  </Button>
                )}
              </div>
            ) : (
            <>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6'>
                {paginatedJobs.map((job: any) => (
                  <JobListingCard
                    key={job.id}
                    job={job}
                    existingApplicationLookupId={findExistingApplicationLookupId(job.url)}
                    isLiteTier={isLiteTier}
                  />
                ))}
              </div>
              {totalPages > 1 && (
                <div className='flex items-center justify-center gap-2'>
                  <button
                    type='button'
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className='flex items-center px-3 py-2 rounded bg-gray-200 text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors'
                    title={t('common.firstPage', 'First page')}
                    aria-label={t('common.firstPage', 'First page')}
                  >
                    <ChevronsLeft className='h-4 w-4' />
                  </button>
                  <button
                    type='button'
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className='flex items-center gap-2 px-4 py-2 rounded bg-gray-200 text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors'
                  >
                    <ChevronLeft className='h-4 w-4' />
                    {t('common.previous', 'Previous')}
                  </button>
                  <span className='text-sm font-medium text-gray-700'>
                    {t('common.page', 'Page')} {currentPage} {t('common.of', 'of')} {totalPages}
                  </span>
                  <button
                    type='button'
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className='flex items-center gap-2 px-4 py-2 rounded bg-gray-200 text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors'
                  >
                    {t('common.next', 'Next')}
                    <ChevronRight className='h-4 w-4' />
                  </button>
                  <button
                    type='button'
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className='flex items-center px-3 py-2 rounded bg-gray-200 text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors'
                    title={t('common.lastPage', 'Last page')}
                    aria-label={t('common.lastPage', 'Last page')}
                  >
                    <ChevronsRight className='h-4 w-4' />
                  </button>
                </div>
              )}
            </>
            )
          ) : !resume ? (
            <>
              <div className='rounded-lg border border-blue-200 bg-blue-50 p-8 text-center'>
                <div className='mb-4 flex justify-center'>
                  <div className='rounded-full bg-blue-100 p-4'>
                    <FileUp className='h-8 w-8 text-blue-600' />
                  </div>
                </div>
                <h3 className='mb-2 text-lg font-semibold text-gray-900'>
                  {t('dashboard.uploadResumeTitle', 'Ready to get personalized job matches?')}
                </h3>
                <p className='mb-6 text-gray-700'>
                  {t(
                    'dashboard.uploadResumeDescription',
                    'Upload your resume to enable AI-powered job matching. We\'ll analyze your skills and experience to find the best opportunities for you.'
                  )}
                </p>
                <div className='flex flex-col gap-3 sm:flex-row sm:justify-center'>
                  <Button className='gap-2' onClick={() => setUploadDialogOpen(true)}>
                    <FileUp className='h-4 w-4' />
                    {t('dashboard.uploadResume', 'Upload Resume')}
                  </Button>
                  <Button
                    variant='outline'
                    onClick={() => {
                      window.location.href = 'mailto:alerts@trackplicant.com?subject=Resume';
                    }}
                  >
                    {t('dashboard.emailResume', 'Or email to alerts@trackplicant.com')}
                  </Button>
                </div>
              </div>

              {/* Resume upload dialog */}
              <ResumeUploadDialog
                open={uploadDialogOpen}
                onOpenChange={setUploadDialogOpen}
                onSuccess={async () => {
                  setUploadDialogOpen(false);
                  // Invalidate resume and job queries to ensure data is fresh
                  await Promise.all([
                    utils.users.getResume.invalidate(),
                    utils.jobListings.getRecommendedJobs.invalidate(),
                  ]);
                  navigate({ to: '/dashboard/profile' });
                }}
              />
            </>
          ) : (
            <div className='rounded-lg border border-amber-200 bg-amber-50 p-8 text-center'>
              <div className='mb-4 flex justify-center'>
                <div className='rounded-full bg-amber-100 p-4'>
                  <Sparkles className='h-8 w-8 text-amber-600' />
                </div>
              </div>
              <h3 className='mb-2 text-lg font-semibold text-gray-900'>
                {t('dashboard.noJobsYetTitle', 'Great! Your resume is ready to work for you')}
              </h3>
              <p className='mb-4 text-gray-700'>
                {t(
                  'dashboard.noJobsYetDescription',
                  'We\'re actively searching for the perfect opportunities that match your skills and experience. Come back soon to discover amazing job matches tailored just for you!'
                )}
              </p>
              <p className='text-sm text-amber-700 font-medium'>
                {t('dashboard.checkBackSoon', 'Check back soon — your next great opportunity is waiting!')}
              </p>
            </div>
          )}
        </>
      )}
    </>
  );
}
